import {
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ReactNode } from "react";

const HEADER_COMPACT_BREAKPOINT_PX = 690;

/** True below the width where chat headers collapse to icons; pass as `isMobile`. */
export const useIsHeaderCompact = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down(HEADER_COMPACT_BREAKPOINT_PX));
};

type AAHeaderButtonProps = {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  tooltip: string;
  variant?: "outlined" | "contained";
  isMobile: boolean;
  "data-testid"?: string;
  "data-attr"?: string;
};

/**
 * Header action for the AtomAssist toolbar.
 *
 * `variant="contained"` is the single primary action (New chat) and keeps a
 * labeled button on desktop. Everything else renders as a quiet icon button
 * with an always-on tooltip, so secondary actions read as one tidy cluster
 * instead of a row of competing labeled buttons.
 */
const AAHeaderButton = ({
  icon,
  label,
  onClick,
  tooltip,
  variant = "outlined",
  isMobile,
  "data-testid": testId,
  "data-attr": dataAttr,
}: AAHeaderButtonProps) => {
  if (variant === "contained" && !isMobile) {
    return (
      <Button
        disableElevation
        variant="contained"
        color="primary"
        startIcon={icon}
        onClick={onClick}
        sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        data-testid={testId}
        data-attr={dataAttr}
      >
        {label}
      </Button>
    );
  }

  return (
    <Tooltip title={tooltip} placement="bottom" enterDelay={300}>
      <IconButton
        size="small"
        onClick={onClick}
        aria-label={label}
        data-testid={testId}
        data-attr={dataAttr}
        sx={{
          color: "text.secondary",
          "&:hover": { color: "primary.main", bgcolor: "action.hover" },
          "& svg": { fontSize: 20 },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};

export default AAHeaderButton;
